/* eslint-disable react/forbid-prop-types */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import { TextField, Box, InputAdornment } from '@mui/material';
import { makeStyles } from '@mui/styles';
import Skeleton from '@mui/material/Skeleton';
import SearchIcon from '@mui/icons-material/Search';

import colors from '../../../constants/colors';

const fixedWidth = 250;
const fixedHeight = 65;

const CustomTextField = ({ 
  title, value, handleChange, placeholder, disabled, multiline, rows, type, format, maxLength, min, max, inputIcon, mandatory, width, loading }) => {
  const useStyles = makeStyles(() => ({
    row: {
      display: 'block',
      margin: '6px 0'
    },
    fieldBox: {
      display: 'flex',
      alignItems: 'center',
    },
    fieldTitle: {
      fontSize: '1.4rem',
      marginBottom: '3px',
      fontStyle: 'normal',
      fontWeight: '500',
      color: '#263238',
    },
    iconMandatory: {
      color: colors.warning,
      paddingLeft: '0.25em',
      fontFamily: 'Roboto, RobotoDraft',
      fontSize: '1.6rem',
      lineHeight: 1,
    },
    textField: {
      width: `${width}`,
      borderRadius: '4px',
      '& .MuiOutlinedInput-root': {
        height: multiline ? 'inherit' : '36px',
      },
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: '#D5D5D5',
      },
      '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: '#aaaaaa',
      },
      '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: '#1a5fa8',
        borderWidth: '1.5px',
      },
    },
    disabled: {
      background: colors.disabled,
      width: `${width}`,
      borderRadius: '4px',
      '& .MuiOutlinedInput-root': {
        height: multiline ? 'inherit' : '36px',
      },
    },
    countField: {
      fontSize: '1rem',
      color: colors.grey,
      display: 'block'
    },
    countFieldWarning: {
      fontSize: '1rem',
      color: colors.warning,
      display: 'block'
    }
  }));
  const classes = useStyles('');

  const [requiredField, setRequiredField] = useState(true);

  const checkValue = valueToCheck => {
    if (valueToCheck){
      setRequiredField(false);
    } else {
      setRequiredField(true);
    }
  }

  const handleSelectedChange = (event) => {
    if (type !== 'date') {
      checkValue(event.target.value);
    }

    handleChange(event.target.value);
  }

  const endAdornment = inputIcon && (
    <InputAdornment position='end'>
        <SearchIcon />
    </InputAdornment>
);

  const getTextField = () => {
    if (type !== 'date') {
      return (
        <div>
          <TextField
            InputLabelProps={{ style: { pointerEvents: 'auto' } }}
            id='custom-textfield-pg'
            placeholder={placeholder}
            value={value}
            onChange={handleSelectedChange}
            className={!disabled ? classes.textField : classes.disabled}
            InputProps={{ endAdornment }}
            inputProps={{ maxLength, min, max }}
            multiline={multiline || false}
            minrows={rows}
            disabled={disabled || false}
            type={type}
            format={format}
            required={mandatory}
            variant='outlined'
          />
          {maxLength &&
            <span className={value.length === maxLength ? classes.countFieldWarning : classes.countField}>
              Characters Left: {' '}
              {`${value.length ? maxLength - value.length : maxLength}`}
            </span>
          }
        </div>
      );
    }

    return (
      <form className={classes.container} noValidate>
        <TextField
          id='custom-textfield-pg'
          type='date'
          value={value}
          className={!disabled ? classes.textField : classes.disabled}
          onChange={handleSelectedChange}
          variant='outlined'
          inputProps={{ min }}
          format={format}
        />
      </form>
    );
  }

  useEffect(() => {
    checkValue(value);
  }, [value]);

  return (
    <>
      <Box className={classes.row}>
        {loading ?
          <>
            <Skeleton width={fixedWidth} />
            <Skeleton width={fixedWidth} height={fixedHeight} />
          </>
          : <>
            <div className={classes.fieldBox}>
              <div className={classes.fieldTitle}>{title}</div>
              {mandatory && requiredField && <span className={classes.iconMandatory}>*</span>}
            </div>
            {getTextField()}
          </>
        }
      </Box>
    </>
  )
}

CustomTextField.propTypes = {
  title: PropTypes.string,
  value: PropTypes.string,
  handleChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string.isRequired,
  maxLength: PropTypes.number,
  min: PropTypes.string,
  max: PropTypes.string,
  disabled: PropTypes.bool,
  multiline: PropTypes.bool,
  rows: PropTypes.number,
  type: PropTypes.string,
  format: PropTypes.string,
  mandatory: PropTypes.bool,
  loading: PropTypes.bool,
  inputIcon: PropTypes.bool,
  width: PropTypes.string
}

CustomTextField.defaultProps = {
  title: null,
  value: null,
  maxLength: null,
  min: null,
  max: null,
  disabled: false,
  multiline: false,
  rows: null,
  type: null,
  format: null,
  mandatory: false,
  loading: false,
  width: '100%',
  inputIcon: false,
}

export default CustomTextField
